import { COLLAB_SESSION_REVOKED_EVENT, createAccessBurst, changesViewerAccess } from '@papan/types'
import { onSessionEnd } from '@papan/core-ui/ui/sessionEnded'
import { COLLAB_FILE_DELETED_EVENT, COLLAB_SESSION_REFUSED_EVENT, COLLAB_LARGE_CSV_EVENT } from './session-events.js'
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import * as Y from 'yjs'
import * as encoding from 'lib0/encoding'
import * as decoding from 'lib0/decoding'
import { devOfflineState, useRealtime } from '@papan/realtime/client'
import { useShareWsToken } from '@papan/files/client/file-io'
import { armConnectTimeout, createReconnectController } from '@papan/reconnect'
import { getApiBase } from '@papan/api-client'
import { saveOfflineDoc, loadOfflineDoc, deleteOfflineDoc } from './offlineStore.js'

// Must match server protocol constants
const MSG_SYNC = 0
const MSG_AWARENESS = 1
const MSG_SAVE = 2
const MSG_ROOM_EPOCH = 3  // Room identity — detects stale Y.Doc from recreated rooms
const MSG_DIRTY = 4  // Server → client: dirty state broadcast
const MSG_DISCARD = 5  // Client → server: discard unsaved changes
const MSG_BULK_UPDATE_START = 6  // Server → client: suppress observers, bulk update incoming
const MSG_BULK_UPDATE_END = 7    // Server → client: bulk update done, rebuild UI
const MSG_CONFLICT = 8           // Server → client: disk changed while room dirty (1 = conflict)
const MSG_FORCE_SAVE = 10        // Client → server: save Y.Doc content, bypass hash check (overwrite)
const MSG_WB_ADD_SHEET = 11      // Client → server: add a sheet to workbook group
const MSG_WB_REMOVE_SHEET = 12   // Client → server: remove a sheet from workbook group
const MSG_WB_RESPONSE = 13       // Server → client: response/error for workbook group operations
const MSG_FILE_STATE = 14        // Server → client (#823/#1315): the file's state — 'being-deleted', 'access-lowered', 'read-only' or '' (cleared)
// (9 is a retired id — never reuse it.)
const MSG_CSV_STATE = 15         // Server → client (#981): a large CSV's state, JSON `{ state: 'saving' | 'reload' }`
/** #823: the editor's session-ended announcements (`session-events.ts`, dependency-free for the surfaces). */
export { COLLAB_FILE_DELETED_EVENT, COLLAB_SESSION_REFUSED_EVENT, COLLAB_LARGE_CSV_EVENT }
/** #823: the server closed the room because the file was moved to the bin (it, or a folder above it). */
const COLLAB_DELETED_CLOSE_CODE = 4410
/**
 * #981: the server refused this WHOLE-FILE room — the file is a large CSV, which opens through its fragment rooms
 * (`large_csv`). The surface holding the editor remounts it as the large grid (`COLLAB_LARGE_CSV_EVENT`).
 */
export const COLLAB_LARGE_CSV_CLOSE_CODE = 4413
/** #1518 D10: the server refuses a connection-minted link's room join with this code (mirrors server connection-link-room.ts). */
export const COLLAB_CONNECTION_LINK_CLOSE_CODE = 4451

/**
 * #823 — the file's lifecycle, as the editor shows it: `deleted` — moved to the bin (`by`: who, a person's name, or
 * null), the last text stays on screen read-only and the socket never reconnects; `being-deleted` — a folder above
 * it is being deleted (#781), read-only until it finishes (or fails); `read-only` — this person's access changed to
 * view only (lowered mid-session, or joined view-only), so edits are off until it is raised again (#1315).
 */
export type CollabFileState = { kind: 'deleted'; by: string | null } | { kind: 'being-deleted' } | { kind: 'read-only' } | null

/** A MSG_WB_RESPONSE payload (workbook Add / Remove / Relink sheet): `error` is what the client branches on; `message`
 *  is the server's own sentence where it has one (a frozen / identity-only tenant). */
export interface WbResponse {
  status: string
  error?: string
  code?: string
  message?: string
  [key: string]: unknown
}

/**
 * #1315 — what this hook instance has been told about the room's access (a ref, reset when the room changes).
 * `lowered`: told the access was lowered. `joinedWritable`: a join of this room said '' (writable), so a later
 * 'read-only' join means access went DOWN even when 'access-lowered' never arrived (the client's own 4002 reconnect).
 */
export interface FileStateMemory { lowered: boolean; joinedWritable: boolean }

/**
 * #1315 I1 (browser check, phase 6) — what this TAB knows about each room's access, kept per room OUTSIDE the editor
 * instance: an access change can remount the editor (the host re-renders it read-only), and a fresh instance with a
 * fresh memory would read the rejoin's 'read-only' as "joined as a viewer" — locked, but never told why. Same room in
 * the same tab: the memory carries over; a viewer who was never writable here still gets no banner. It belongs to the
 * SESSION that learned it: a sign-out, another account, an identity or tenant switch (`onSessionEnd`) forgets it all —
 * else the next person in this tab would be told "your access changed" about access they never had. The key is the
 * room STRING, which carries the route's own id (a `.chain` row or a compound `s_<shared>_<id>` differs from the native
 * id), so writable through one route never reads as "lowered" on another; share sessions get no access frames at all
 * (`joinFileStates` / `applyRecheckedAccess` are native-client only).
 */
const fileStateMemory = new Map<string, FileStateMemory>()
onSessionEnd(() => { fileStateMemory.clear() })
const NO_FILE_STATE_MEMORY: FileStateMemory = { lowered: false, joinedWritable: false }
/** Test seam: forget what this tab knew (each test starts a fresh tab). */
export function __resetFileStateMemoryForTests(): void { fileStateMemory.clear() }

/**
 * #1315 Task 12 — what a MSG_SAVE ack shows the saver (`saveError` → `useSaveRefusalToast`): its reason, whenever it
 * carries one — a refusal's sentence, or a SUCCESS ack's note (a sheet changed on disk was not overwritten). No reason →
 * nothing. `ok` never suppresses a reason: the server sends one only to the saver, only when there is something to say.
 */
export function saveAckNote(reason: string): string | null {
  return reason ? reason : null
}

/** Folds one MSG_FILE_STATE frame into the file state. Pure and idempotent (StrictMode double-invokes updaters).
 *  The returned memory (`lowered` / `joinedWritable`) must stay independent of `prev`: the hook advances the memory ref
 *  outside the state updater, from a possibly one-render-old fileStateRef. */
export function nextFileState(
  prev: CollabFileState,
  state: string,
  mem: FileStateMemory,
): { next: CollabFileState; mem: FileStateMemory } {
  if (prev?.kind === 'deleted') return { next: prev, mem }
  if (state === 'being-deleted') return { next: { kind: 'being-deleted' }, mem }
  const lock = (): CollabFileState => (prev?.kind === 'being-deleted' ? prev : { kind: 'read-only' })
  if (state === 'access-lowered') return { next: lock(), mem: { ...mem, lowered: true } }
  if (state === 'read-only') {
    if (mem.lowered || mem.joinedWritable) return { next: lock(), mem: { ...mem, lowered: true } }
    return { next: prev, mem }
  }
  if (state === '') return { next: null, mem: { lowered: false, joinedWritable: true } }
  return { next: null, mem }
}

/**
 * #1315 A — the ONE reader and advancer of `fileStateMemory`: folds a MSG_FILE_STATE frame of `room` into `prev`
 * (`nextFileState`) and advances the room's memory. Every editor that receives the frames goes through it — the live
 * editors (useYjs) and the large-CSV session — so they share what this tab knows about the room. Idempotent: a second
 * fold of the same frame, over the memory the first one left, gives the same state and memory, so a repeated call (a
 * StrictMode-doubled updater) is harmless.
 */
export function foldFileState(room: string, prev: CollabFileState, state: string): CollabFileState {
  const r = nextFileState(prev, state, fileStateMemory.get(room) ?? NO_FILE_STATE_MEMORY)
  fileStateMemory.set(room, r.mem)
  return r.next
}

/** #1315 A — this tab was told the room's access was LOWERED (the read-only grid's banner, after its session closed). */
export function wasLoweredHere(room: string): boolean {
  return (fileStateMemory.get(room) ?? NO_FILE_STATE_MEMORY).lowered
}

/**
 * #1315 A — the host re-rendered a live editor of `room` read-only from a fresh PERMISSION answer before the server's
 * frame arrived (the refetch beat it): lowered — only when a join of this room said writable here (`joinedWritable`),
 * so a viewer who was never writable is never told "your access changed".
 */
export function markLoweredHere(room: string): void {
  const mem = fileStateMemory.get(room)
  if (mem?.joinedWritable) fileStateMemory.set(room, { ...mem, lowered: true })
}

/** The `deleted[:name]` close reason → who moved it to the bin. */
export function deletedBy(reason: string): string | null {
  return reason.startsWith('deleted:') ? reason.slice('deleted:'.length) || null : null
}
const SYNC_STEP1 = 0
const SYNC_STEP2 = 1
const SYNC_UPDATE = 2

/** Base delay for the per-user WS cap (4008) retry, before it escalates toward
 *  20s. Named because it is reset from THREE places — the ref's init, the
 *  accepted-connection reset (MSG_ROOM_EPOCH), and the connection-effect start.
 *  As three separate literals they could drift, and two resets disagreeing would
 *  quietly resurrect the stale-backoff bug the effect-start reset exists to fix. */
const CAP_BACKOFF_BASE_MS = 3000

/** #1315 B: the longest a Remove Sheet keeps this editor locked while its answer is awaited. */
const REMOVE_PENDING_MAX_MS = 60_000

// When a tab goes inactive (e.g. user switches to another file tab), keep the
// collab WebSocket alive for this long before tearing it down. A quick switch
// back within the window reuses the live connection — no reconnect, no resync,
// no content "blip". Only a genuinely-abandoned tab releases its connection.
// Pairs with the server's room-eviction window (ROOM_EVICTION_MS in
// yjs-handler.ts), which keeps the room warm AFTER the last client finally
// leaves — so a reconnect just past this grace still finds a warm room.
export const DISCONNECT_GRACE_MS = 60_000

interface UseYjsOptions {
  room: string
  /** When false, disconnects the WebSocket and skips reconnecting.
   *  Reconnects when set back to true. Defaults to true. */
  active?: boolean
}

interface UseYjsReturn {
  doc: Y.Doc
  connected: boolean
  synced: boolean
  isDirty: boolean
  /** Disk version changed while room has unsaved edits — user must resolve */
  isConflicted: boolean
  /** Increments when the saved version changes during an active conflict (triggers cache invalidation) */
  conflictGeneration: number
  /** True while a bulk Y.Doc update (discard/agent edit) is in progress.
   *  Use bulkUpdatingRef for synchronous checks in observers. */
  bulkUpdating: boolean
  /** Ref for synchronous observer checks — set immediately on message receipt */
  bulkUpdatingRef: React.MutableRefObject<boolean>
  /** Server rejected the connection with 4403 — user has no access to this file */
  forbidden: boolean
  /** #823: moved to the bin / being deleted (see CollabFileState). */
  fileState: CollabFileState
  /** True from the moment a reconnect forces a fresh Y.Doc (room epoch changed —
   *  stale doc discarded) until that new doc finishes its first sync. Editors use
   *  it to keep the last content painted + show a "reconnecting" indicator instead
   *  of flashing empty (the cache→empty→repaint blip). */
  reloading: boolean
  /** True once the bounded reconnect controller has exhausted its attempt cap and
   *  stopped retrying. Editors surface it as an honest "Disconnected — reload"
   *  instead of a "Reconnecting…" spinner that would never resolve. */
  gaveUp: boolean
  save: () => void
  /**
   * The server's reason for the LAST refused save, or null.
   *
   * `nonce` increments per refusal ack so two identical refusals are
   * distinguishable. Without it a second failure carrying the same sentence —
   * pressing "Overwrite Save" after a refusal, or any manual retry — set state
   * to the same string, React bailed out of the re-render, and the surfacing
   * effect never ran. The one case the reason field was added for was the one
   * that stayed silent.
   */
  saveError: { message: string; nonce: number } | null
  /**
   * #1315 B: the LAST workbook-operation error THIS connection received (MSG_WB_RESPONSE `status: 'error'`), or null —
   * per instance, as `saveError` (`nonce` per frame), so only the editor whose connection was answered toasts
   * (`useWorkbookRefusalToast`). The window event `papan:wb-response` still fires for WorkspaceView's prompts.
   */
  wbError: { payload: WbResponse; nonce: number } | null
  /** #1315 B (task review) — this editor asked to Remove a sheet and its answer hasn't come yet: the server drops this
   *  connection's updates meanwhile (a recalc would write #REF! back), so the editor locks rather than lose what is typed. */
  removePending: boolean
  /** Discard unsaved changes — resets room to last saved state for all clients */
  discard: () => void
  /** Force-save Y.Doc content to disk, bypassing hash check (overwrite external changes) */
  forceSave: () => void
  /** Send workbook group add-sheet message */
  sendWbAddSheet: (payload: { type: 'create' | 'link' | 'copy' | 'merge' | 'relink'; fileName?: string; parentId?: string; fileId?: string; sourceSheetId?: string; mergeWorkbookId?: string; relinkSheetId?: string }) => void
  /** Send workbook group remove-sheet message (JSON payload) */
  sendWbRemoveSheet: (payload: string) => void
}

/** #981: a large CSV's fragment / session room is the file's room plus `?frag=<id|_session>&base=<hash>`. */
export function collabWsUrl(room: string, shareToken: string | null, params?: Record<string, string>): string {
  const url = getWsUrl(room, shareToken)
  if (!params) return url
  const q = new URLSearchParams(params).toString()
  return q ? `${url}${url.includes('?') ? '&' : '?'}${q}` : url
}

function getWsUrl(room: string, shareToken: string | null): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  // API base (#234): VITE_API_URL override → runtime hostname derivation →
  // current host for same-origin deploys (localhost dev).
  const apiBase = getApiBase()
  const apiHost = apiBase ? apiBase.replace(/^https?:\/\//, '').replace(/\/$/, '') : window.location.host
  let url = `${protocol}//${apiHost}/ws/collab/${encodeURIComponent(room)}`
  // Share-view recipients append the share-session token so the
  // server's WebSocket upgrade handler can validate them without a
  // Better Auth cookie. Drive callers don't set this, so the URL
  // stays the same shape it was before and cookie auth flows as today.
  if (shareToken) url += `?share=${encodeURIComponent(shareToken)}`
  return url
}

export function useYjs({ room, active = true }: UseYjsOptions): UseYjsReturn {
  const [connected, setConnected] = useState(false)
  const [synced, setSynced] = useState(false)
  const [isConflicted, setIsConflicted] = useState(false)
  // Why the SERVER refused the last save (full drive, frozen tenant), or null.
  // Distinct from a transport failure: this is an answer, not a silence.
  const [saveError, setSaveError] = useState<{ message: string; nonce: number } | null>(null)
  const saveErrorNonce = useRef(0)
  // #1315 B: a workbook operation this connection was refused (Add / Remove / Relink sheet)
  const [wbError, setWbError] = useState<{ payload: WbResponse; nonce: number } | null>(null)
  const wbErrorNonce = useRef(0)
  const [removePending, setRemovePending] = useState(false)
  const removePendingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Ends `removePending`: its epoch arrived (the server sends it once the Remove has run, after any failed-save ack),
   *  or its sheet response (a refusal before the Remove started, no epoch) — or it never will (the bound). Never a save
   *  ack: every save's ack goes to the whole room, so another person's save would end it while the server still drops
   *  this connection's updates. */
  const removePendingRef = useRef(false)
  const endRemovePending = useCallback(() => {
    if (removePendingTimer.current) { clearTimeout(removePendingTimer.current); removePendingTimer.current = null }
    removePendingRef.current = false
    setRemovePending(false)
  }, [])
  const [forbidden, setForbidden] = useState(false)
  const [fileState, setFileState] = useState<CollabFileState>(null)
  const fileStateRef = useRef<CollabFileState>(null)
  fileStateRef.current = fileState
  // True while a forced Y.Doc rebuild (epoch changed → stale doc discarded) is
  // awaiting its first resync. Editors keep the last content visible during this
  // window instead of flashing empty.
  const [reloading, setReloading] = useState(false)
  // #222 review — the bounded reconnect can give up after the attempt cap. Surface
  // it so the badge shows an honest "Disconnected — reload" instead of a perpetual
  // "Reconnecting…" spinner that never resolves (the controller has stopped).
  const [gaveUp, setGaveUp] = useState(false)
  // `effectiveActive` lags `active` on the true→false edge by DISCONNECT_GRACE_MS
  // (grace period before disconnect) and tracks it instantly on false→true.
  // The connection effect keys on this, not `active`, so a quick tab switch-away
  // and back never tears down the WebSocket. See the grace effect below.
  const [effectiveActive, setEffectiveActive] = useState(active)
  const graceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { onOrgEvent } = useRealtime()
  // When mounted under a ShareWsTokenProvider, the recipient's
  // share-session token rides along the WebSocket as a query param so
  // the server can validate them without Drive cookies. Drive callers
  // don't wrap with the provider; this is null and the URL stays the
  // same as before.
  const shareWsToken = useShareWsToken()

  // (the room's file-state MEMORY is the tab's, per room — `fileStateMemory` — and survives a remount)
  useEffect(() => { setForbidden(false); setReloading(false); fileStateRef.current = null; setFileState(null) }, [room])

  // Grace period before disconnect. When the tab becomes active, connect
  // immediately (cancel any pending teardown). When it goes inactive, keep the
  // connection alive for DISCONNECT_GRACE_MS so a quick switch-back doesn't pay
  // a reconnect + resync (the source of the content "blip" on tab switch).
  useEffect(() => {
    if (active) {
      if (graceTimer.current) { clearTimeout(graceTimer.current); graceTimer.current = null }
      setEffectiveActive(true)
    } else {
      if (graceTimer.current) clearTimeout(graceTimer.current)
      graceTimer.current = setTimeout(() => {
        graceTimer.current = null
        setEffectiveActive(false)
      }, DISCONNECT_GRACE_MS)
    }
    return () => {
      if (graceTimer.current) { clearTimeout(graceTimer.current); graceTimer.current = null }
    }
    // `room` is included so a pending teardown timer is cleared when the editor
    // switches files — otherwise it could fire later and tear down the new
    // room's connection.
  }, [active, room])

  // Live permission revocation: on org:access_changed / role_changed, force the
  // WebSocket to re-validate by closing with code 4002; the reconnect hits the
  // server's connect-time permission check (revoked → 4403 → forbidden).
  //
  // This is the SECOND layer: the collab server re-resolves every connected
  // client itself on each permission change in the document's org OR the
  // client's own (yjs-handler.ts — R34 #171, extended by #1261 to a cross-org
  // editor's own org and a share session's acting / link org): it evicts with
  // 4003 on a lost read and downgrades to read-only on a lost write. So this
  // reconnect runs at most once per interval (`createAccessBurst`, owner D146
  // 2026-10-21): an admin's run of team edits reconnected every open live
  // document once per click, in every member's tab. It still matters for an
  // UPGRADE (the server never raises a session's access): a viewer granted Edit
  // gets write on this reconnect — at most ~5 s later during a run of changes.
  // Accepted (D146): when the server's `permission:changed` publish fails but
  // this org event arrives, a revoke waits on this reconnect (300 ms, or up to
  // ~5 s inside a run) instead of closing at once; the server's 30 s scan backs
  // it. A nested re-share's MIDDLE org is the server scan's alone (#1261) —
  // this tab never hears that org either.
  useEffect(() => {
    if (!room || !effectiveActive) return
    const tenantId = room.split(':')[0]
    const burst = createAccessBurst(() => {
      const ws = wsRef.current
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close(4002, 'permission-revalidate')
      }
    })
    const off = onOrgEvent((event) => {
      if (event.type !== 'org:access_changed' && event.type !== 'org:role_changed') return
      if (event.tenantId !== tenantId) return
      // A change naming ANOTHER member (#1259's per-viewer signal) leaves this viewer's access as it
      // was — every other access reaction already skips it; the server re-checks this editor anyway.
      if (!changesViewerAccess(event)) return
      burst.push()
    })
    // A pending one is dropped with the room: a new room connects fresh (the
    // connect-time check), and the server's own re-check already ran.
    return () => { off(); burst.cancel() }
  }, [onOrgEvent, room, effectiveActive])
  /** Increments when the saved version changes during an active conflict */
  const [conflictGeneration, setConflictGeneration] = useState(0)
  const [isDirty, setIsDirty] = useState(false)
  const [bulkUpdating, setBulkUpdating] = useState(false)
  const bulkUpdatingRef = useRef(false)
  const [generation, setGeneration] = useState(0)  // Incremented when room epoch changes (stale Y.Doc)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Escalating backoff for the per-user WS cap (server 4008). Reset to the base
  // on any accepted connection (MSG_ROOM_EPOCH). Prevents a stuck "Connecting"
  // from hammering the endpoint every 2s while another tab/app holds the budget.
  const capBackoffRef = useRef(CAP_BACKOFF_BASE_MS)
  const roomEpochRef = useRef<string | null>(null)  // Last known room epoch — detects recreated rooms
  const connectStartedAt = useRef<number | null>(null)  // For reload-time tracking: WS open → first sync

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const doc = useMemo(() => new Y.Doc(), [room, generation])  // New doc per room or after invalidation

  // Offline persistence: stored Y.Doc state from IndexedDB, loaded on mount.
  // NOT applied until the server epoch confirms it's safe (prevents CRDT duplication).
  const offlineStateRef = useRef<{ epoch: string; state: Uint8Array; updatedAt: number } | null>(null)
  const offlineSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!room || !effectiveActive) return  // Skip WebSocket for preview mode or inactive editors (after grace)

    let destroyed = false

    // A fresh connection context (this effect re-runs on room / active / token
    // change → a brand-new controller with a fresh attempt budget) is never
    // "given up" — clear the UI flag here so a grace-toggle after a give-up
    // doesn't leave the badge stuck on "Disconnected" while it's actually
    // retrying again. onGiveUp/onopen drive it thereafter. (#222 review follow-up)
    setGaveUp(false)
    // Same reasoning for the 4008 cap backoff: it was only reset on an accepted
    // connection (MSG_ROOM_EPOCH), so a room that never got one leaked its
    // escalated delay into the NEXT room. Hit the per-user WS cap on room A
    // (backoff climbs toward 20s), switch to room B before any connect is
    // accepted → B's first cap retry inherited A's 20s instead of starting at
    // 3s. A fresh context starts at the base delay.
    capBackoffRef.current = CAP_BACKOFF_BASE_MS

    // Bounded reconnect policy — shared with the host realtime socket via
    // @papan/reconnect. Created per connection context (this effect re-runs on
    // room / active / token change, each a legitimately fresh start); the
    // backoff only accumulates across the in-context onclose→reconnect retries
    // below, which reuse the same `connect` closure. Replaces the old fixed
    // 2s-forever reconnect that let a stuck tab storm a dead endpoint (an
    // observed CF-quota incident). Jitter is on: many clients edit the same
    // doc, so an origin blip drops them together — jitter de-syncs the herd.
    const reconnect = createReconnectController({
      jitter: 0.25,
      log: (msg) => console.log(`[Collab ${new Date().toISOString()}] ${room}: ${msg}`),
      onGiveUp: () => {
        console.error(
          `[Collab ${new Date().toISOString()}] ${room}: reconnect attempts exhausted — giving up (reload to retry)`,
        )
        setGaveUp(true)
      },
    })

    // Load offline state from IndexedDB BEFORE opening the socket.
    //
    // This read used to race the handshake: `connect()` fired immediately, and
    // MSG_ROOM_EPOCH — the ONLY place stored state is consumed — read a ref the
    // read had frequently not filled yet. Losing that race silently DISCARDED
    // the user's offline edits: the epoch handler saw null and did nothing, and
    // the clean room's MSG_DIRTY(0) then deleted the row (see the MSG_DIRTY
    // case). Whether a user's offline work survived came down to whether an
    // IndexedDB read beat a WebSocket handshake — which on a warm connection it
    // usually did not.
    //
    // Ordering, not just timing, is why this has to gate the connect rather
    // than be awaited at the consumption point: the restore must land before
    // SYNC_STEP1 goes out, so the state vector we send already accounts for it.
    // Awaiting inside the message handler would let SYNC_STEP2 be applied first.
    //
    // Capped by a timeout so a wedged IndexedDB (a blocked upgrade in another
    // tab, a browser with storage disabled) can never leave the editor
    // permanently unconnected. Hitting the cap degrades to the old behaviour —
    // a possibly-skipped restore — rather than to a dead socket.
    let offlineConsumed = false
    let offlineReadSettled = false
    // Whether this connection has seen the room dirty. The offline row is
    // retired by a clean verdict only AFTER one — i.e. by an actual save.
    let sawDirty = false
    const OFFLINE_LOAD_TIMEOUT_MS = 2000
    let offlineLoadTimer: ReturnType<typeof setTimeout> | null = null
    void Promise.race([
      loadOfflineDoc(room).then(stored => {
        // Never re-arm a slot the epoch handler already consumed. A late
        // resolve that overwrote that null handed a stale FULL-doc state to the
        // next reconnect, re-injecting content the room had moved past — and
        // marking dirty a room nobody had edited.
        if (destroyed || offlineConsumed) return
        offlineStateRef.current = stored
      }),
      new Promise<void>(resolve => { offlineLoadTimer = setTimeout(resolve, OFFLINE_LOAD_TIMEOUT_MS) }),
    ]).catch(() => { /* IndexedDB unavailable — connect without a restore */ })
      .finally(() => {
        if (offlineLoadTimer) { clearTimeout(offlineLoadTimer); offlineLoadTimer = null }
        offlineReadSettled = true
        connect()
      })

    // Clears the current socket's handshake timeout (armConnectTimeout). Held at
    // effect scope so the teardown and dev-offline paths can clear it too.
    let clearConnectTimeout: (() => void) | null = null

    function connect() {
      if (destroyed || devOfflineState.offline) return
      // #823: a file moved to the bin stays closed — a reconnect (the tab coming back after its grace, a token
      // change) would only be refused and replace the last text the person may still be copying
      if (fileStateRef.current?.kind === 'deleted') return
      // The offline read gates the FIRST connect (see above), but connect() has
      // other callers — the dev offline toggle's subscriber, the reconnect
      // timer — and they do not know about that gate. One firing inside the
      // read's window would open a socket that races the restore, defeating the
      // gate; and because the assignment to `wsRef.current` below simply
      // overwrites, the earlier socket becomes unreachable — never closed, but
      // still running its keep-alive and its onclose reconnect. Refuse both.
      if (!offlineReadSettled) return
      const live = wsRef.current
      if (live && (live.readyState === WebSocket.CONNECTING || live.readyState === WebSocket.OPEN)) return

      sawDirty = false
      connectStartedAt.current = (typeof performance !== 'undefined' ? performance.now() : Date.now())
      const ws = new WebSocket(getWsUrl(room, shareWsToken))
      ws.binaryType = 'arraybuffer'
      wsRef.current = ws
      // Bound the handshake: a CF edge stall leaves the socket in CONNECTING with
      // no open/error/close ever, and every retry below is close-driven — so the
      // editor sat on "Connecting..." forever. The timeout close()s the socket;
      // the browser then fires onclose (1006) → the normal bounded
      // `reconnect.schedule(connect)` branch retries (same backoff + attempt cap).
      clearConnectTimeout?.()
      const clearThisConnectTimeout = armConnectTimeout(ws, {
        log: (msg) => console.warn(`[Collab ${new Date().toISOString()}] ${room}: ${msg}`),
      })
      clearConnectTimeout = clearThisConnectTimeout

      let pingInterval: ReturnType<typeof setInterval> | null = null

      ws.onopen = () => {
        clearThisConnectTimeout()
        if (destroyed) { ws.close(); return }
        setConnected(true)
        setGaveUp(false)  // a fresh connection clears the terminal give-up state
        // Arm the stable-gate: if this connection stays up, the backoff resets.
        reconnect.markConnected()
        // Connected — no log needed (disconnect is still logged for debugging)
        // Don't send SYNC_STEP1 here — wait for server's MSG_ROOM_EPOCH first.
        // This ensures we check the epoch before sending any Y.Doc state,
        // preventing stale CRDT items from contaminating a recreated room.

        // Keep-alive ping every 30s to prevent Cloudflare/proxy idle timeout (100s)
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(new Uint8Array([99]))  // Ping — msgType 99 is unused, ignored by server's switch
          }
        }, 30_000)
      }

      ws.onmessage = (event) => {
        const data = new Uint8Array(event.data as ArrayBuffer)
        const decoder = decoding.createDecoder(data)
        const msgType = decoding.readVarUint(decoder)

        switch (msgType) {
          case MSG_SYNC: {
            const syncType = decoding.readVarUint(decoder)
            switch (syncType) {
              case SYNC_STEP1: {
                // Server sent its state vector — reply with our diff
                const serverSV = decoding.readVarUint8Array(decoder)
                const diff = Y.encodeStateAsUpdate(doc, serverSV)
                const encoder = encoding.createEncoder()
                encoding.writeVarUint(encoder, MSG_SYNC)
                encoding.writeVarUint(encoder, SYNC_STEP2)
                encoding.writeVarUint8Array(encoder, diff)
                ws.send(encoding.toUint8Array(encoder))
                break
              }
              case SYNC_STEP2: {
                // Server sent its diff — apply and mark synced
                const update = decoding.readVarUint8Array(decoder)
                Y.applyUpdate(doc, update, 'server')
                setSynced(true)
                setReloading(false)  // Fresh doc has resynced — safe to show it (swap from frozen cache)
                // Reload-time tracking: how long from opening the WS to first
                // sync. This is the user-visible "blip" window on reconnect.
                if (connectStartedAt.current !== null) {
                  const now = (typeof performance !== 'undefined' ? performance.now() : Date.now())
                  const ms = Math.round(now - connectStartedAt.current)
                  connectStartedAt.current = null
                  console.log(`[Collab ${new Date().toISOString()}] Synced ${room} in ${ms}ms`)
                }
                break
              }
              case SYNC_UPDATE: {
                // Incremental update from another client via server
                const update = decoding.readVarUint8Array(decoder)
                Y.applyUpdate(doc, update, 'server')
                break
              }
            }
            break
          }
          case MSG_AWARENESS: {
            // Awareness updates (cursor positions, etc.) — ignored for now
            break
          }
          case MSG_SAVE: {
            // Server responded to save request — dirty state handled by MSG_DIRTY.
            //
            // The ack now carries the REASON when the server REFUSED (a full
            // drive, a frozen tenant). Without it a refused save was
            // indistinguishable from one that simply had not happened yet: no
            // route, no 413, no `onError` — the explanation only ever reached
            // the server log.
            //
            // Both trailing fields are optional so an older server (which sent
            // only the message type) still parses: `readVarUint` throws past
            // the buffer, so guard on `hasContent`.
            try {
              if (decoding.hasContent(decoder)) decoding.readVarUint(decoder) // the ok flag — the reason alone decides
              const reason = decoding.hasContent(decoder) ? decoding.readVarString(decoder) : ''
              const note = saveAckNote(reason)
              setSaveError(note === null ? null : { message: note, nonce: ++saveErrorNonce.current })
            } catch { /* older server, no payload — nothing to report */ }
            break
          }
          case MSG_DIRTY: {
            // Server broadcasts authoritative dirty state
            const dirty = decoding.readVarUint(decoder) === 1
            // Retire the offline row only on a clean verdict that FOLLOWS a
            // dirty one — that pairing is a save, which is the event the row
            // was always meant to outlive.
            //
            // Not simply "any clean verdict": the server announces the room's
            // CURRENT state on connect, and for a clean room that lands before
            // our SYNC_STEP2 has been processed — so acting on it discarded the
            // very offline edits we had just restored and were still sending.
            // Nor "after we sent SYNC_STEP2": the server's send order is
            // ROOM_EPOCH → SYNC_STEP1 → awareness → MSG_DIRTY, so we reply to
            // STEP1 (and would set such a flag) BEFORE that MSG_DIRTY is even
            // read — a guard that is always true by the time it is consulted.
            if (!dirty && room && sawDirty) deleteOfflineDoc(room).catch(() => {})
            if (dirty) sawDirty = true
            setIsDirty(dirty)
            if (!dirty) setIsConflicted(false)  // Conflict resolved when room becomes clean
            break
          }
          case MSG_CONFLICT: {
            // Disk version changed while room has unsaved edits
            const conflicted = decoding.readVarUint(decoder) === 1
            if (conflicted) setConflictGeneration(n => n + 1)
            setIsConflicted(conflicted)
            break
          }
          case MSG_BULK_UPDATE_START: {
            // Server is about to send a bulk Y.Doc reset (discard or agent edit).
            // Set ref immediately so observers skip synchronously during Y.applyUpdate.
            if (import.meta.env.DEV) console.log(`[Collab ${new Date().toISOString()}] BULK_UPDATE_START received`)
            bulkUpdatingRef.current = true
            setBulkUpdating(true)
            break
          }
          case MSG_BULK_UPDATE_END: {
            // Bulk update complete — editors should rebuild UI from Y.Doc.
            // Ref stays true until after the rebuild effect runs.
            if (import.meta.env.DEV) console.log(`[Collab ${new Date().toISOString()}] BULK_UPDATE_END received`)
            setBulkUpdating(false)
            break
          }
          case MSG_ROOM_EPOCH: {
            endRemovePending()
            // #823: every connect starts from a clean state — a join under a folder still being deleted says so again
            setFileState(prev => (prev?.kind === 'deleted' ? prev : null))
            // Server sends this on connect, before SYNC_STEP1.
            // If the epoch changed, the room was recreated (file-first reload).
            // Our Y.Doc has stale CRDT items that would cause duplication on merge.
            // Solution: discard this connection, create a fresh Y.Doc, reconnect.
            const epoch = decoding.readVarString(decoder)

            if (roomEpochRef.current !== null && roomEpochRef.current !== epoch) {
              // Epoch mismatch — room was recreated since our last connection
              console.log(`[Collab ${new Date().toISOString()}] Room epoch changed (${roomEpochRef.current.slice(0, 8)} → ${epoch.slice(0, 8)}), refreshing Y.Doc`)
              roomEpochRef.current = epoch
              offlineStateRef.current = null
              deleteOfflineDoc(room).catch(() => {})  // Discard stale offline state
              ws.close()
              setSynced(false)  // Reset so Univer waits for new sync before init
              setReloading(true)  // Keep last content painted until the fresh doc resyncs (no empty flash)
              setGeneration(g => g + 1)  // Creates fresh Y.Doc via useMemo
              // Notify parent components (e.g. TabContent) to re-resolve room key
              window.dispatchEvent(new CustomEvent('papan:collab-epoch-changed', { detail: { room } }))
              return
            }

            // First connection or same epoch — safe to sync. The server
            // accepted us (not capped), so reset the 4008 backoff.
            roomEpochRef.current = epoch
            capBackoffRef.current = CAP_BACKOFF_BASE_MS

            // Restore offline state if epoch matches AND the stored state is old
            // enough to be real offline edits (not a brief HMR disconnect save).
            const stored = offlineStateRef.current
            const MIN_OFFLINE_AGE_MS = 5000
            if (stored && stored.epoch === epoch && (Date.now() - stored.updatedAt > MIN_OFFLINE_AGE_MS)) {
              try {
                Y.applyUpdate(doc, stored.state, 'offline-restore')
                console.log(`[Collab ${new Date().toISOString()}] Restored offline edits for ${room} (epoch ${epoch.slice(0, 8)}, age ${Math.round((Date.now() - stored.updatedAt) / 1000)}s)`)
              } catch (err) {
                console.warn(`[Collab ${new Date().toISOString()}] Failed to restore offline state:`, err)
              }
            } else if (stored) {
              console.log(`[Collab ${new Date().toISOString()}] Skipped offline restore — too recent (${Date.now() - stored.updatedAt}ms, likely HMR)`)
              deleteOfflineDoc(room).catch(() => {})
            }
            offlineStateRef.current = null  // Consumed — don't re-apply
            offlineConsumed = true          // ...and don't let a late read re-arm it

            const sv = Y.encodeStateVector(doc)
            const encoder = encoding.createEncoder()
            encoding.writeVarUint(encoder, MSG_SYNC)
            encoding.writeVarUint(encoder, SYNC_STEP1)
            encoding.writeVarUint8Array(encoder, sv)
            ws.send(encoding.toUint8Array(encoder))
            break
          }
          case MSG_FILE_STATE: {
            const state = decoding.readVarString(decoder)
            // The memory is advanced NOW (a remount before the next render must not lose it); the updater folds again
            // from the real `prev` — `foldFileState` is idempotent, so its second advance changes nothing.
            foldFileState(room, fileStateRef.current, state)
            setFileState(prev => foldFileState(room, prev, state))
            break
          }
          case MSG_CSV_STATE: {
            // #981: only a large CSV's fragment / session rooms send it; the large-CSV session hook acts on it.
            decoding.readVarString(decoder)
            break
          }
          case MSG_WB_RESPONSE: {
            endRemovePending()
            const payloadStr = decoding.readVarString(decoder)
            try {
              const payload = JSON.parse(payloadStr)
              // #1315 B: kept on THIS instance too — the window event names no room, and every mounted spreadsheet hears it
              if (payload && payload.status === 'error') setWbError({ payload: payload as WbResponse, nonce: ++wbErrorNonce.current })
              window.dispatchEvent(new CustomEvent('papan:wb-response', { detail: payload }))
            } catch { /* ignore parse errors */ }
            break
          }
        }
      }

      ws.onclose = (event) => {
        clearThisConnectTimeout()
        if (pingInterval) { clearInterval(pingInterval); pingInterval = null }
        // Only clear state if this WebSocket is still the current one.
        // React Strict Mode creates ws2 before ws1.onclose fires async —
        // without this guard, ws1.onclose would overwrite wsRef.current (ws2) with null.
        if (wsRef.current !== ws) return // Stale WebSocket — ignore entirely
        console.log(`[Collab ${new Date().toISOString()}] Disconnected from ${room} (code=${event.code}, reason=${event.reason || 'none'})`)
        setConnected(false)
        setSynced(false)
        wsRef.current = null
        // Any drop cancels the pending stable-gate — a connection that dropped
        // before proving stable must NOT reset the backoff.
        reconnect.onDropped()

        if (!destroyed) {
          if (event.code === 4001) {
            // Room invalidated (e.g. file extension changed on rename).
            // Create a fresh Y.Doc before reconnecting so the old CRDT
            // items don't merge with the server's new content.
            roomEpochRef.current = null  // Reset so epoch check doesn't double-trigger
            setGeneration(g => g + 1)
          } else if (event.code === COLLAB_DELETED_CLOSE_CODE) {
            // #823: moved to the bin. Don't reconnect; the document keeps its last text (read-only on screen) so the
            // person can copy out what they were writing.
            setReloading(false)
            fileStateRef.current = { kind: 'deleted', by: deletedBy(event.reason || '') }
            setFileState(fileStateRef.current)
            // the surface holding this editor (a workspace tab) greys itself — and remounts it on a restore
            window.dispatchEvent(new CustomEvent(COLLAB_FILE_DELETED_EVENT, { detail: { room } }))
          } else if (event.code === COLLAB_LARGE_CSV_CLOSE_CODE) {
            // #981: a large CSV — this editor can't hold it; never reconnect, the surface remounts the large grid.
            setReloading(false)
            window.dispatchEvent(new CustomEvent(COLLAB_LARGE_CSV_EVENT, { detail: { room } }))
          } else if (event.code === 4403) {
            // Permission denied — user lost access. Don't reconnect.
            // Clear `reloading` too: it's only otherwise cleared on SYNC_STEP2,
            // which never arrives on a 4403, so without this the editor would be
            // wedged read-only behind the ForbiddenOverlay.
            setReloading(false)
            setForbidden(true)
            window.dispatchEvent(new CustomEvent(COLLAB_SESSION_REFUSED_EVENT, { detail: { room } }))
          } else if (event.code === COLLAB_CONNECTION_LINK_CLOSE_CODE) {
            // #1518 D10: a connection-minted link never joins a room. Never reconnect; the share view reads the
            // file over /s/ instead. Clear `reloading` (SYNC_STEP2 never arrives) and tell the surface.
            setReloading(false)
            window.dispatchEvent(new CustomEvent(COLLAB_SESSION_REFUSED_EVENT, { detail: { room } }))
          } else if (event.code === 4002) {
            // Permission revalidate — user's org permission changed mid-session,
            // or (sent by the server) the account revoked its sessions (#601).
            // Reconnect immediately so the server's connect-time checks either
            // accept (still has access) or refuse (4403 now hidden, 401 signed
            // out). The Y.Doc is kept, so a surviving device loses no edits.
            // #809: a REVOCATION close is also announced to the page, so the surface
            // that owns the session (the share view) can tell "logged out" from a blip.
            if (event.reason === 'session_revoked') window.dispatchEvent(new CustomEvent(COLLAB_SESSION_REVOKED_EVENT))
            reconnectTimer.current = setTimeout(connect, 0)
          } else if (event.code === 4008) {
            // Per-user WS connection cap hit. Other sockets (tabs / sandbox
            // apps / half-open zombies still inside the server's 70s freshness
            // window) are using the budget. Retrying every 2s just churns
            // rejected upgrades and never frees a slot faster — back off with
            // escalating delay so we retry GENTLY and self-heal once a slot
            // frees (no manual reload needed). Reset on the next accepted
            // connect (MSG_ROOM_EPOCH above).
            const delay = capBackoffRef.current
            capBackoffRef.current = Math.min(Math.round(capBackoffRef.current * 1.6), 20_000)
            console.warn(`[Collab ${new Date().toISOString()}] Connection cap (4008) for ${room} — retry in ${delay}ms`)
            reconnectTimer.current = setTimeout(connect, delay)
          } else {
            // Normal disconnect — reconnect with bounded exponential backoff
            // (same Y.Doc). The epoch check on reconnect is the safety net — if
            // the room was recreated while we were disconnected, the epoch
            // mismatch triggers a fresh Y.Doc automatically. The controller
            // gives up after the attempt cap so a dead endpoint can't loop
            // forever.
            reconnect.schedule(connect)
          }
        }
      }

      ws.onerror = () => {
        // onclose will fire after this
      }
    }

    // Listen for local doc updates and send to server
    const updateHandler = (update: Uint8Array, origin: unknown) => {
      if (origin === 'server' || origin === 'offline-restore') return // Don't echo back
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return

      const encoder = encoding.createEncoder()
      encoding.writeVarUint(encoder, MSG_SYNC)
      encoding.writeVarUint(encoder, SYNC_UPDATE)
      encoding.writeVarUint8Array(encoder, update)
      ws.send(encoding.toUint8Array(encoder))
    }

    // Debounced offline persistence: save Y.Doc to IndexedDB when WS is
    // disconnected.  Online tabs skip the save (server is authoritative).
    // Saves eagerly (no delay) so no edits are lost if the tab closes.
    // The RESTORE side filters out stale HMR saves (see epoch handler).
    const offlineSaveHandler = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return

      if (offlineSaveTimer.current) clearTimeout(offlineSaveTimer.current)
      offlineSaveTimer.current = setTimeout(() => {
        const epoch = roomEpochRef.current
        if (!epoch) return
        const state = Y.encodeStateAsUpdate(doc)
        saveOfflineDoc(room, epoch, state).catch(() => {})
      }, 1000)
    }
    doc.on('update', offlineSaveHandler)
    doc.on('update', updateHandler)

    // connect() is called once the offline read above settles — see its comment.

    // Dev offline toggle: disconnect/reconnect collab WS when toggled
    const unsubOffline = devOfflineState.subscribe((offline) => {
      if (offline) {
        reconnect.cancel()
        clearConnectTimeout?.()
        if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null }
        if (wsRef.current) { wsRef.current.close(); wsRef.current = null }
      } else {
        connect()
      }
    })

    return () => {
      destroyed = true
      unsubOffline()
      doc.off('update', updateHandler)
      doc.off('update', offlineSaveHandler)
      if (offlineSaveTimer.current) clearTimeout(offlineSaveTimer.current)
      if (offlineLoadTimer) clearTimeout(offlineLoadTimer)
      reconnect.cancel()
      clearConnectTimeout?.()
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
      if (wsRef.current) wsRef.current.close()
    }
  }, [doc, room, effectiveActive, shareWsToken])

  // NOTE: We intentionally do NOT destroy doc in a separate cleanup effect.
  // React 18 Strict Mode runs effect cleanups between double-invoke, which would
  // destroy the doc while useMemo preserves the same object for the second mount.
  // The doc is garbage-collected when the component truly unmounts.

  // Send save message to server
  const save = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    if (removePendingRef.current) return  // #1315 B: this editor's Remove Sheet is running — locked until it answers

    const encoder = encoding.createEncoder()
    encoding.writeVarUint(encoder, MSG_SAVE)
    ws.send(encoding.toUint8Array(encoder))
  }, [])

  // Send discard message to server — resets room to last saved state
  const discard = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    if (removePendingRef.current) return  // #1315 B: this editor's Remove Sheet is running — locked until it answers

    const encoder = encoding.createEncoder()
    encoding.writeVarUint(encoder, MSG_DISCARD)
    ws.send(encoding.toUint8Array(encoder))
  }, [])

  // Force-save: overwrite disk version with Y.Doc content (bypasses hash check)
  const forceSave = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    if (removePendingRef.current) return  // #1315 B: as save()

    const encoder = encoding.createEncoder()
    encoding.writeVarUint(encoder, MSG_FORCE_SAVE)
    ws.send(encoding.toUint8Array(encoder))
  }, [])

  // Send workbook group add-sheet message
  const sendWbAddSheet = useCallback((payload: { type: 'create' | 'link' | 'copy' | 'merge' | 'relink'; fileName?: string; parentId?: string; fileId?: string; sourceSheetId?: string; mergeWorkbookId?: string; relinkSheetId?: string }) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return

    const encoder = encoding.createEncoder()
    encoding.writeVarUint(encoder, MSG_WB_ADD_SHEET)
    encoding.writeVarString(encoder, JSON.stringify(payload))
    ws.send(encoding.toUint8Array(encoder))
  }, [])

  // Send workbook group remove-sheet message
  const sendWbRemoveSheet = useCallback((payload: string) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return

    const encoder = encoding.createEncoder()
    encoding.writeVarUint(encoder, MSG_WB_REMOVE_SHEET)
    encoding.writeVarString(encoder, payload)
    ws.send(encoding.toUint8Array(encoder))
    // Locked until its answer or its epoch; a Remove the server ignores (no group, the last sheet) answers nothing, so a
    // bound ends it too
    if (removePendingTimer.current) clearTimeout(removePendingTimer.current)
    removePendingTimer.current = setTimeout(endRemovePending, REMOVE_PENDING_MAX_MS)
    removePendingRef.current = true
    setRemovePending(true)
  }, [endRemovePending])
  useEffect(() => () => { if (removePendingTimer.current) clearTimeout(removePendingTimer.current) }, [])
  useEffect(() => { endRemovePending() }, [room, endRemovePending])

  return { doc, connected, synced, isDirty, isConflicted, saveError, wbError, removePending, conflictGeneration, bulkUpdating, bulkUpdatingRef, forbidden, fileState, reloading, gaveUp, save, discard, forceSave, sendWbAddSheet, sendWbRemoveSheet }
}
